import type { TypographyVariantsOptions } from '@mui/material/styles';

// VisionHeight DS (new Figma) uses ONE family — Aeonik — across every variant.
// The numeric scale is mostly MUI's default typography, so we set fontFamily
// once (it cascades to every variant) and override only what differs.
// Do NOT assume an omitted variant matches — check its Figma token before
// relying on it. The list at the bottom is what has NOT been checked.
// One family only — Aeonik. (The old Aeonik Fono display face is gone: the new
// DS is single-family, so wordmark/headings use Aeonik too.)
export const AEONIK = "'Aeonik', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
// Monospace for ids, IPs and code.
export const MONO = "'JetBrains Mono', 'Fira Code', ui-monospace, SFMono-Regular, Menlo, monospace";

export const typography: TypographyVariantsOptions = {
  fontFamily: AEONIK, // cascades to all variants (Figma: Aeonik everywhere)
  h1: { fontWeight: 400 }, // Figma: 96px/400 (MUI default weight is 300)
  h2: { fontWeight: 400 }, // Figma: 60px/400 (MUI default weight is 300)
  // Figma token `typography/h3` = Aeonik Medium 48px / 1.167. MUI's h3 is already
  // 48/1.167, so only the weight differs (its default is 400).
  h3: { fontWeight: 500 },
  // Figma token `typography/h4`: Aeonik Medium 32px / 1.235 / 0.25. MUI's h4 is
  // 34px at weight 400, so BOTH differ — the weight was missed the first time
  // and every page title in the app has been rendering Regular since.
  h4: { fontSize: '2rem', fontWeight: 500 },
  // Buttons: sentence-case, plus tracking. Figma's three sizes are S 13px /
  // M 14px / L 15px at weight 500, and `typography/buttonLarge` measures
  // 0.46px of tracking — which MUI does NOT supply: its `button` variant is the
  // one variant with no `letterSpacing` at all, so this rendered as `normal`
  // until it was measured. In em so it holds across the three sizes (0.46px at
  // 15px, 0.43 at 14, 0.40 at 13 — the DS's "~0.4px" for the smaller two).
  button: { textTransform: 'none', letterSpacing: '0.0307em' },
  // The small uppercase label at the top of a card ("TRIAGED DETECTION").
  overline: { display: 'block', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.5 },
};

// ── The DS names are NOT MUI's names ──────────────────────────────────────────
//
// Read this before typing a variant you saw in Figma. The DS body scale is
// shifted one step from MUI's, so the same word means a different size in each:
//
//   Figma token          size   the MUI variant that renders it
//   typography/body1     18px   (none — no MUI variant is 18px)
//   typography/body2     16px   body1
//   typography/body3     14px   body2      ← what the screens' text is bound to
//
// So a design that says "body3" is `variant="body2"` in code, and a design that
// says "body2" is `variant="body1"`. Typing the name you read is how you get a
// size nobody asked for. The values are NOT realigned to the DS names on
// purpose: doing so would resize all 92 `body2` call sites in the app.
//
// ── What has actually been checked against the DS ─────────────────────────────
//
//   h4          WRONG, fixed above — the DS draws it Medium, MUI defaults to 400
//   h6          20 / 500 / 1.6 / 0.15px  — identical to MUI's default, left alone
//   caption     12 / 400 / 1.66 / 0.4px  — identical to MUI's default, left alone
//   subtitle1   16px                     — matches MUI (line-height not read)
//   body2       14 / 1.43, matching DS body3, EXCEPT tracking: the DS says
//               0.17px and MUI gives 0.15px. Deliberately not chased — 0.02px
//               per character is invisible, and changing it would reflow 92 call
//               sites and bury the real diffs in the visual-regression run.
//   body1       16px, matching DS body2, EXCEPT line-height 1.5 vs the DS's
//               1.48 (0.32px). Same call, same reason.
//
// Still unread, and in this order if it ever matters: subtitle2 (3 uses), h5 and
// h3 (1 each). `overline` is not on the list — all 17 of its uses are Storybook
// stories, none is product code.

